import type { FormEventHandler, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, FileSignature, Info } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useSocialLinkingPlatforms } from '@/hooks/use-social-linking-platforms';

type Props = {
  onSubmit: FormEventHandler<HTMLFormElement>;
  url: string;
  onUrlChange: (value: string) => void;
  platform: string | null;
  blockedReason: string | null;
  blockedAction: ReactNode;
  canSubmit: boolean;
  acceptedTerms: boolean;
  onAcceptTerms: () => void;
  submitted: boolean;
  ranking?: boolean;
  importAction?: ReactNode;
  automaticImportControl?: ReactNode;
};

/**
 * A pasted Facebook link may come from a connected Page (verified on submission)
 * or not (reviewed by an admin). Viewers who cannot link Facebook yet only get
 * the admin-review text. Mounted only for Facebook ranking links, so the
 * linking-platforms query runs only then.
 */
function FacebookSubmissionHelp() {
  const { t } = useTranslation();
  const canLinkFacebook = useSocialLinkingPlatforms().includes('facebook');
  if (!canLinkFacebook) {
    return <p className="mb-4 text-sm text-muted-foreground">{t('ranking.facebookReportHelp')}</p>;
  }
  return <p className="mb-4 text-sm text-muted-foreground">
    {t('ranking.facebookHelp')}{' '}
    <button type="button" className="underline" onClick={() => window.dispatchEvent(new CustomEvent('autoclipper:open-account-settings', { detail: { section: 'social', platform: 'facebook' } }))}>{t('ranking.connectFacebookPage')}</button>
  </p>;
}

/** Shared URL admission form; campaign handlers retain ownership and terms checks. */
export function CampaignClipSubmissionForm({ onSubmit, url, onUrlChange, platform, blockedReason, blockedAction, canSubmit, acceptedTerms, onAcceptTerms, submitted, ranking = false, importAction, automaticImportControl }: Props) {
  const { t } = useTranslation();
  return (
    <Card id="submission-form" className="relative overflow-hidden p-6">
      <h2 className="text-xl font-semibold mb-1">{t('campaign.submit.title')}</h2>
      <p className="text-sm text-muted-foreground mb-4">{t('campaign.submit.instruction')}</p>
      {ranking && platform === 'facebook' && <FacebookSubmissionHelp />}
      <form
        onSubmit={onSubmit}
        className={`space-y-3 ${!acceptedTerms ? 'pointer-events-none select-none opacity-50' : ''}`}
        aria-hidden={!acceptedTerms}
      >
        {automaticImportControl}
        <div className="relative">
          <Input
            type="url"
            required
            placeholder="https://…"
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            className={platform ? 'pr-28' : ''}
          />
          {platform && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[10px] font-bold tracking-wide px-2 py-1 rounded bg-primary/15 text-primary">
              <CheckCircle2 className="w-3 h-3" />
              {platform.toUpperCase()}
            </span>
          )}
        </div>
        {url && !platform && (
          <p className="text-xs text-amber-500">
            {t(ranking ? 'ranking.clipSubmission.unsupportedUrl' : 'campaign.submit.unsupportedUrl')}
          </p>
        )}
        {blockedReason && (
          <p className="text-sm text-amber-600 mt-1">
            {blockedReason}{' '}
            {blockedAction}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button type="submit" disabled={!canSubmit}>
                {t('campaign.submit.submit')}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-[220px] text-xs text-center">
            <Info className="w-3 h-3 inline mr-1 mb-0.5" />
            {t(ranking ? 'ranking.clipSubmission.periodHelp' : 'campaign.submit.submitTooltip')}
          </TooltipContent>
        </Tooltip>
        {importAction}
        </div>
      </form>
      {submitted && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-sm text-foreground">{t(ranking ? 'ranking.clipSubmission.received' : 'campaign.submit.afterSubmitMessage')}</p>
        </div>
      )}
      {!acceptedTerms && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 px-6 py-8 bg-card/85 backdrop-blur-sm text-center">
          <div className="space-y-1 max-w-md">
            <h3 className="font-semibold text-base">{t('campaign.terms.overlayTitle')}</h3>
            <p className="text-sm text-muted-foreground">{t('campaign.terms.overlayBody')}</p>
          </div>
          <Button
            onClick={onAcceptTerms}
            size="lg"
            className="h-14 px-10 text-base font-semibold gap-2 shadow-md"
          >
            <FileSignature className="w-5 h-5" />
            {t('campaign.terms.overlayCta')}
          </Button>
        </div>
      )}
    </Card>
  );
}
